import React from 'react';
import { FileText, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BillValidationData {
  hasData: boolean;
  billPeriod: string;
  billKwh: number;
  calculatedKwh: number;
  match: boolean;
  difference: number;
  percentDiff: number;
  recordsFound: number;
  // Demand validation fields
  billDemandKw?: number;
  calculatedDemandKw?: number;
  demandMatch?: boolean;
  demandDifference?: number;
  demandPercentDiff?: number;
}

interface BillValidationProps {
  validation: BillValidationData | null;
}

export const BillValidation: React.FC<BillValidationProps> = ({ validation }) => {
  const { t } = useTranslation();
  if (!validation) return null;

  const hasDemandData = validation.billDemandKw && validation.billDemandKw > 0;
  const allMatch = validation.match && (!hasDemandData || validation.demandMatch);

  const getStatusColor = () => {
    if (allMatch) return 'bg-green-50 border-green-200';
    if (validation.hasData) return 'bg-yellow-50 border-yellow-200';
    return 'bg-gray-50 border-gray-200';
  };

  const getStatusIcon = () => {
    if (allMatch) {
      return <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />;
    }
    if (validation.hasData) {
      return <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />;
    }
    return <AlertCircle className="w-5 h-5 text-gray-500 mt-0.5" />;
  };

  const formatPeriod = (period: string) => {
    if (period && period.includes('-')) {
      const [year, month] = period.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    }
    return period;
  };

  return (
    <div className={`mb-6 p-4 rounded-lg border ${getStatusColor()}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          {getStatusIcon()}
        </div>
        <div className="flex-1">
          <h5 className="text-sm font-semibold text-swiss-charcoal mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t('dataAlignment.billValidation.title')}
          </h5>
          {validation.hasData ? (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-swiss-stone">{t('dataAlignment.billValidation.billPeriod')}:</span>
                <span className="text-sm font-medium">
                  {formatPeriod(validation.billPeriod)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-swiss-stone">{t('dataAlignment.billValidation.billConsumption')}:</span>
                <span className="text-sm font-medium">{validation.billKwh.toLocaleString()} kWh</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-swiss-stone">{t('dataAlignment.billValidation.calculatedFromData')}:</span>
                <span className="text-sm font-medium">{validation.calculatedKwh.toLocaleString()} kWh</span>
              </div>
              {!validation.match && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-swiss-stone">{t('dataAlignment.billValidation.difference')}:</span>
                  <span className="text-sm font-medium text-yellow-700">
                    {validation.difference.toLocaleString()} kWh ({validation.percentDiff.toFixed(1)}%)
                  </span>
                </div>
              )}

              {/* Demand Validation Section */}
              {hasDemandData && (
                <>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-swiss-stone font-semibold">{t('dataAlignment.billValidation.peakDemandCheck')}:</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-swiss-stone">{t('dataAlignment.billValidation.billPeakDemand')}:</span>
                    <span className="text-sm font-medium">{validation.billDemandKw?.toLocaleString()} kW</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-swiss-stone">{t('dataAlignment.billValidation.calculatedPeakDemand')}:</span>
                    <span className="text-sm font-medium">{validation.calculatedDemandKw?.toLocaleString()} kW</span>
                  </div>
                  {!validation.demandMatch && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-swiss-stone">{t('dataAlignment.billValidation.difference')}:</span>
                      <span className="text-sm font-medium text-yellow-700">
                        {validation.demandDifference?.toFixed(1)} kW ({validation.demandPercentDiff?.toFixed(1)}%)
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Overall Status */}
              {allMatch ? (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <p className="text-sm text-green-700 font-medium">
                    ✓ {t('dataAlignment.billValidation.perfectMatch')}
                  </p>
                </div>
              ) : (
                <div className="mt-2 pt-2 border-t border-yellow-200">
                  <p className="text-sm text-yellow-700">
                    ⚠ {t('dataAlignment.billValidation.mismatchDetected', {
                      type: !validation.match && !validation.demandMatch && hasDemandData
                        ? t('dataAlignment.billValidation.energyAndDemand')
                        : !validation.match
                        ? t('dataAlignment.billValidation.energyConsumption')
                        : t('dataAlignment.billValidation.peakDemand')
                    })}
                  </p>
                </div>
              )}
              <div className="text-xs text-swiss-stone mt-1">
                {t('dataAlignment.billValidation.recordsAnalyzed')}: {validation.recordsFound.toLocaleString()}
              </div>
            </div>
          ) : (
            <p className="text-sm text-swiss-stone">
              {t('dataAlignment.billValidation.noDataFound', { period: validation.billPeriod })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};